import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import { Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { dataService } from "../../../Services/data/data.service";
import { flowBoxDeviceStatus } from "../../../Config/constants";
import { useFlowBoxHub } from "../../../hooks/useFlowBoxHub";
import HeaderBar from "../../Shared/Header/HeaderBar";
import HeaderTitle from "../../Shared/Header/HeaderTitle";
import Button from "../../Shared/Buttons/Button";
import EmptyStateCard from "../../Shared/EmptyStateCard";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../Shared/Select";
import { toast } from "react-toastify";
import RegisterFlowBoxDeviceDialog from "./RegisterFlowBoxDeviceDialog";
import FlowBoxDeviceConfigureDialog from "./FlowBoxDeviceConfigureDialog";
import AssignFlowBoxImeiDialog from "./AssignFlowBoxImeiDialog";
import AnyDeviceCard, { SkeletonCard } from "./FlowBoxDeviceCard";
import { flowBoxErrorMessage } from "./flowBoxErrors";

// ──────────────────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────────────────
function FlowBoxDeviceOverviewPage(props) {
  const classes = usePageStyles();
  const { t } = useTranslation();
  const userId = props.Login.userid;
  const companyIds = props.Login.companyIds || [];

  const STATUS_OPTIONS = [
    { id: flowBoxDeviceStatus.PENDING_CONFIGURATION, label: t("flowBox.status.pendingConfiguration"), chipVariant: "warning" },
    { id: flowBoxDeviceStatus.REGISTERED,            label: t("flowBox.status.searching"),            chipVariant: "warning" },
    { id: flowBoxDeviceStatus.AVAILABLE_TO_CONNECT,  label: t("flowBox.status.availableToConnect"),   chipVariant: "info" },
    { id: flowBoxDeviceStatus.SETUP,                 label: t("flowBox.status.setup"),                chipVariant: "info" },
    { id: flowBoxDeviceStatus.ACTIVE,                label: t("flowBox.status.active"),               chipVariant: "success" },
    { id: flowBoxDeviceStatus.DEREGISTERED,          label: t("flowBox.status.deregistered"),         chipVariant: "danger" },
    { id: flowBoxDeviceStatus.INACTIVE,              label: t("flowBox.status.inactive"),             chipVariant: "default" },
  ];

  const [allDevices, setAllDevices] = useState([]);
  const [devices, setDevices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [companyFilter, setCompanyFilter] = useState(null);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [configureDeviceId, setConfigureDeviceId] = useState(null);
  const [assignImeiDevice, setAssignImeiDevice] = useState(null);

  useFlowBoxHub(companyIds, (deviceId, statusId) => {
    setAllDevices((prev) => {
      const idx = prev.findIndex((d) => d.id === deviceId);
      if (idx === -1) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx], statusId };
      return updated;
    });
  });

  const loadDevices = () => {
    const uniqueCompanyIds = [...new Set(companyIds)];
    if (uniqueCompanyIds.length === 0) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      dataService.getAllActiveCompaniesByUserId(userId),
      ...uniqueCompanyIds.map((id) => dataService.getFlowBoxDevicesByCompanyId(id)),
    ])
      .then(([companiesResult, ...deviceResults]) => {
        setCompanies(companiesResult || []);
        const combined = deviceResults.flat().filter(Boolean);
        const unique = [...new Map(combined.map((d) => [d.id, d])).values()];
        // Hide deregistered devices from the main view — they've been removed
        const visible = unique.filter((d) => d.statusId !== flowBoxDeviceStatus.DEREGISTERED);
        visible.sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
        setAllDevices(visible);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadDevices(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let filtered = [...allDevices];
    if (nameFilter) {
      const q = nameFilter.toLowerCase();
      filtered = filtered.filter((d) =>
        (d.displayName || "").toLowerCase().includes(q) || (d.imei || "").toLowerCase().includes(q)
      );
    }
    if (statusFilter !== null) {
      filtered = filtered.filter((d) => d.statusId === statusFilter.id);
    }
    if (companyFilter !== null) {
      filtered = filtered.filter((d) => d.companyId === companyFilter.id);
    }
    setDevices(filtered);
  }, [allDevices, nameFilter, statusFilter, companyFilter]);

  const handleDeregister = (device) => {
    dataService.deregisterFlowBoxDevice({ deviceId: device.id, updatedByUserId: userId })
      .then(() => { toast.success(t("flowBox.overview.deviceRemoved")); loadDevices(); })
      .catch((err) => toast.error(flowBoxErrorMessage(err, t, "flowBox.overview.deviceRemoveFailed")));
  };

  const handleDeactivate = (device) => {
    dataService.deactivateFlowBoxDevice({ deviceId: device.id, updatedByUserId: userId })
      .then(() => { toast.success(t("flowBox.overview.deviceDeactivated")); loadDevices(); })
      .catch((err) => toast.error(flowBoxErrorMessage(err, t, "flowBox.overview.deviceDeactivateFailed")));
  };

  const handleConnect = (device) => {
    return dataService.connectFlowBoxDevice({ deviceId: device.id, updatedByUserId: userId })
      .then(() => { toast.success(t("flowBox.overview.deviceConnectedSetup")); loadDevices(); })
      .catch((err) => { toast.error(flowBoxErrorMessage(err, t, "flowBox.overview.deviceConnectFailed")); });
  };

  const handleCancelSearch = (device) => {
    dataService.cancelFlowBoxDeviceSearch({ deviceId: device.id, updatedByUserId: userId })
      .then(() => { toast.success(t("flowBox.overview.searchCancelled")); loadDevices(); })
      .catch((err) => toast.error(flowBoxErrorMessage(err, t, "flowBox.overview.searchCancelFailed")));
  };

  const openRegister = () => setRegisterOpen(true);

  return (
    <React.Fragment>
      <Box className="page-container">
        <HeaderBar>
          <HeaderTitle>{t("flowBox.overview.title")}</HeaderTitle>
        </HeaderBar>

        {/* Toolbar */}
        <div className={classes.toolbar}>
          <div className={classes.toolbarStart}>
            <div className={classes.searchContainer}>
              <input
                placeholder={t("flowBox.overview.searchPlaceholder")}
                className={classes.searchInput}
                type="text"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
              />
              <Search size={16} className={classes.searchIcon} />
            </div>
            <div className={classes.filterContainer}>
              <Select value={statusFilter?.id} onChange={setStatusFilter} size="small">
                <SelectTrigger>{statusFilter?.label || t("flowBox.overview.allStatuses")}</SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>{t("flowBox.overview.allStatuses")}</SelectItem>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.id} value={o}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {companies.length > 1 && (
              <div className={classes.filterContainer}>
                <Select value={companyFilter?.id} onChange={setCompanyFilter} size="small">
                  <SelectTrigger>{companyFilter?.name || t("flowBox.overview.allCompanies")}</SelectTrigger>
                  <SelectContent>
                    <SelectItem value={null}>{t("flowBox.overview.allCompanies")}</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className={classes.toolbarEnd}>
            <Button size="small" startIcon={<Plus size={14} />} onClick={openRegister}>
              {t("flowBox.overview.registerDevice")}
            </Button>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className={classes.grid}>
            {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
          </div>
        ) : devices.length === 0 ? (
          <EmptyStateCard
            title={t("flowBox.overview.noDevicesFound")}
            description={allDevices.length === 0
              ? t("flowBox.overview.registerFirstDevice")
              : t("flowBox.overview.noDevicesMatchFilter")}
            actionLabel={allDevices.length === 0 ? t("flowBox.overview.registerDevice") : undefined}
            onActionClick={allDevices.length === 0 ? openRegister : undefined}
          />
        ) : (() => {
          const transient = devices.filter((d) =>
            d.statusId === flowBoxDeviceStatus.PENDING_CONFIGURATION ||
            d.statusId === flowBoxDeviceStatus.REGISTERED ||
            d.statusId === flowBoxDeviceStatus.AVAILABLE_TO_CONNECT
          );
          const established = devices.filter((d) =>
            d.statusId !== flowBoxDeviceStatus.PENDING_CONFIGURATION &&
            d.statusId !== flowBoxDeviceStatus.REGISTERED &&
            d.statusId !== flowBoxDeviceStatus.AVAILABLE_TO_CONNECT
          );
          const cardProps = (device) => ({
            key: device.id,
            device,
            onConfigure: (d) => setConfigureDeviceId(d.id),
            onDeregister: handleDeregister,
            onDeactivate: handleDeactivate,
            onConnect: handleConnect,
            onCancelSearch: handleCancelSearch,
            onAssignImei: (d) => setAssignImeiDevice(d),
          });
          return (
            <>
              {transient.length > 0 && (
                <div className={classes.grid}>
                  {transient.map((d) => <AnyDeviceCard {...cardProps(d)} />)}
                </div>
              )}
              {transient.length > 0 && established.length > 0 && (
                <div className={classes.sectionDivider} />
              )}
              {established.length > 0 && (
                <div className={classes.grid}>
                  {established.map((d) => <AnyDeviceCard {...cardProps(d)} />)}
                </div>
              )}
            </>
          );
        })()}
      </Box>

      <RegisterFlowBoxDeviceDialog
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onRegistered={loadDevices}
        userId={userId}
        companies={companies}
      />

      <FlowBoxDeviceConfigureDialog
        open={configureDeviceId !== null}
        onClose={() => setConfigureDeviceId(null)}
        deviceId={configureDeviceId}
        userId={userId}
        onSaved={loadDevices}
      />

      <AssignFlowBoxImeiDialog
        open={assignImeiDevice !== null}
        onClose={() => setAssignImeiDevice(null)}
        onAssigned={loadDevices}
        userId={userId}
        device={assignImeiDevice}
      />
    </React.Fragment>
  );
}

export default FlowBoxDeviceOverviewPage;

// ──────────────────────────────────────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────────────────────────────────────
const usePageStyles = makeStyles(() => ({
  toolbar: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap-reverse",
    marginBottom: "8px",
  },
  toolbarStart: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginRight: "16px",
  },
  toolbarEnd: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  searchContainer: {
    width: "240px",
    position: "relative",
  },
  searchInput: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    padding: "0px 8px",
    paddingRight: "32px",
    borderRadius: "4px",
    border: "1px solid #DCDCDC",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",
    backgroundColor: "#FFFFFF",
    "&:focus": {
      outline: "none",
      border: "1px solid var(--color-primary)",
    },
  },
  searchIcon: {
    color: "#0C0E29",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: "8px",
    pointerEvents: "none",
  },
  filterContainer: {
    width: "auto",
    minWidth: "fit-content",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "16px",
    marginBottom: "16px",
  },
  sectionDivider: {
    height: "1px",
    backgroundColor: "#EDEDED",
    margin: "8px 0 16px",
  },
}));
